// accountMenuItems.js
//
// DAS-2878: the 'alert_effectiveness' item carries a data-testid on its label
// (an inline span) so the DR_testing_ticket_auditor's `verify: ui` marker has
// a stable click target reaching two menus deep — the antd `data-menu-id` is
// per-mount and not selector-stable across page loads.
//
// DAS-2557: Pure builder for the Account.jsx dropdown menu items.
// Extracted from Account.jsx so the gating logic (which items show for which
// user) is a pure function of its inputs and can be unit-tested in isolation.
//
// The "half the options" bug (DAS-2557) was caused by the menu reading a
// userId that lagged behind isLoggedIn. Keeping this a pure function of
// (isLoggedIn, userId, nodeEnv) makes that class of regression testable: the
// admin items must appear whenever userId === 2, full stop.

import React from 'react';

/**
 * Build the Ant Design Dropdown `items` array for the account menu.
 *
 * @param {Object}  params
 * @param {boolean} params.isLoggedIn  Redux auth.isLoggedIn
 * @param {number|null} params.userId  Redux auth.userId (set atomically with isLoggedIn)
 * @param {string}  [params.nodeEnv]   process.env.NODE_ENV (defaults to it)
 * @returns {Array<{key: string, label: string}>}
 */
export function buildAccountMenuItems({ isLoggedIn, userId, nodeEnv = process.env.NODE_ENV }) {
  return [
    ...(!isLoggedIn ? [
      {
        key: 'login',
        label: 'Login'
      },
      {
        key: 'register',
        label: 'Register'
      }
    ] : [
      {
        key: 'investment_strategy',
        // DAS-3026/3047: stable click target so the auditor's `verify: ui` can
        // reach the Plan settings panel inside this modal (same pattern as
        // alert_effectiveness below; antd's data-menu-id is not selector-stable).
        label: <span data-testid="account-menu-investment-strategy">Investment Strategy</span>
      },
      {
        key: 'prime_objective',
        label: 'Prime Objective'
      },
      {
        key: 'category_preferences',
        label: 'Category Preferences'
      },
      {
        key: 'data_preferences',
        label: 'Data Preferences'
      },
      {
        key: 'broker_access',
        label: 'Broker Access'
      },
      {
        // DAS-2992: release notes of promoted tickets (testid for verify: ui)
        key: 'release_notes',
        label: <span data-testid="account-menu-release-notes">Releasenotes</span>
      },
      {
        // DAS-3157: the alpha register — found alphas, how they are used, research that found nothing
        key: 'alpha',
        label: <span data-testid="account-menu-alpha">Alpha</span>
      },
      {
        // DAS-3237: the map of what produces the morning advice, generated from the code
        key: 'advice_pipeline',
        label: <span data-testid="account-menu-advice-pipeline">Advice pipeline</span>
      },
      {
        key: 'logout',
        label: 'Logout'
      },
      ...(userId === 1 || userId === 2 ? [{
        key: 'maintenance',
        // DAS-2997: stable click target for the auditor's `verify: ui` path
        label: <span data-testid="account-menu-maintenance">Maintenance</span>
      }] : []),
      ...(userId === 2 ? [
        {
          key: 'market_alerts',
          label: 'Market Alerts'
        },
        {
          key: 'clauses',
          label: 'Clauses'
        },
        {
          key: 'alert_scopes',
          label: 'Alert-Scopes'
        },
        {
          key: 'alert_effectiveness',
          label: <span data-testid="account-menu-alert-effectiveness">Alert Effectiveness</span>
        },
        {
          key: 'alert_discovery',
          label: 'Alert Discovery'
        },
        {
          key: 'portfolio_data_management',
          label: 'Portfolio Data Management'
        },
        {
          key: 'script_reporting',
          label: 'Script Reporting'
        },
        {
          key: 'ticker_maintenance',
          label: 'Ticker Maintenance'
        },
        {
          key: 'redux_view',
          label: 'Redux'
        },
        {
          key: 'database_schema_search',
          label: 'Find Databases, Tables, Fields'
        },
        {
          key: 'llm_models',
          label: 'LLM Models'
        },
        {
          key: 'degiro_spike',
          label: 'DeGiro spike'
        }
      ] : []),
      ...(userId === 1 || userId === 2 ? [{
        key: 'esg_checker',
        label: 'ESG Checker'
      }, {
        key: 'trendsearch_management',
        label: 'Trendsearch'
      }] : []),
      // DAS-1788: Backtesting menu item (userId === 2) - now works on PROD
      ...(userId === 2 ? [{
        key: 'backtesting',
        label: 'Backtesting'
      }] : []),
      // DAS-1389: AI Ideas menu item (dev only)
      ...(nodeEnv === 'development' ? [{
        key: 'ai_ideas',
        label: '💡 AI Ideas (Dev)'
      }] : []),
      // DAS-2207: Security Audit (dev only, userId 2)
      ...(nodeEnv === 'development' && userId === 2 ? [{
        key: 'security_audit',
        label: '🛡️ Security Audit (Dev)'
      }] : []),
      // DAS-3094: Agentic Overview — what the AI agents cost (dev only, userId 2)
      ...(nodeEnv === 'development' && userId === 2 ? [{
        key: 'agentic_overview',
        label: '🤖 Agentic Overview (Dev)'
      }] : []),
      // Drift Watch — agent instruction-drift demo (dev only, userId 2)
      ...(nodeEnv === 'development' && userId === 2 ? [{
        key: 'drift_watch',
        label: '🧭 Drift Watch (Dev)'
      }] : [])
    ])
  ];
}

export default buildAccountMenuItems;
